import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchMentionableUsers } from '@/hooks/useTickets';

interface MentionableUser {
  id: string;
  displayName: string;
  email: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  mentionedUserIds: string[];
  onMentionedUserIdsChange: (ids: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function MentionInput({
  value,
  onChange,
  mentionedUserIds,
  onMentionedUserIdsChange,
  placeholder,
  className,
  disabled,
}: MentionInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [users, setUsers] = useState<MentionableUser[]>([]);
  const [usersFetched, setUsersFetched] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);

  // Fetch users lazily on first @ trigger
  const ensureUsers = useCallback(async () => {
    if (usersFetched) return;
    try {
      const res = await fetchMentionableUsers();
      setUsers(res.data);
    } catch {
      // Silently fail -- user can still type
    }
    setUsersFetched(true);
  }, [usersFetched]);

  // Filter users by query
  const filtered = query
    ? users.filter(
        (u) =>
          u.displayName.toLowerCase().includes(query.toLowerCase()) ||
          u.email.toLowerCase().includes(query.toLowerCase()),
      )
    : users;

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filtered.length, query]);

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const newValue = e.target.value;
    onChange(newValue);

    const cursorPos = e.target.selectionStart;
    // Look backwards from cursor to find an active @ trigger
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const atIdx = textBeforeCursor.lastIndexOf('@');

    if (atIdx !== -1) {
      // Check that @ is at start or preceded by whitespace
      const charBefore = atIdx > 0 ? textBeforeCursor[atIdx - 1] : ' ';
      const afterAt = textBeforeCursor.slice(atIdx + 1);
      // The text after @ should not contain spaces (user is still typing the name)
      // Allow spaces in names for multi-word names but stop at newlines
      if (/\s/.test(charBefore) || atIdx === 0) {
        // Only treat as an active mention query while the text after @ is a
        // single token. Once it contains whitespace the mention is considered
        // complete — this stops the dropdown reopening after a multi-word name
        // (e.g. "@Anouk Henfling ") is inserted.
        if (!/\s/.test(afterAt) && afterAt.length <= 40) {
          setQuery(afterAt);
          setMentionStart(atIdx);
          setShowDropdown(true);
          ensureUsers();
          return;
        }
      }
    }

    setShowDropdown(false);
    setQuery('');
    setMentionStart(null);
  }

  function selectUser(user: MentionableUser) {
    if (mentionStart === null) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    const before = value.slice(0, mentionStart);
    const cursorPos = textarea.selectionStart;
    const after = value.slice(cursorPos);
    // Insert the human-readable name; it is converted back to an @{id} token at
    // submit time (see lib/mentions.ts). The user id is tracked separately via
    // mentionedUserIds below and drives notifications.
    const token = `@${user.displayName} `;
    const newValue = before + token + after;

    onChange(newValue);

    // Add user id if not already in the list
    if (!mentionedUserIds.includes(user.id)) {
      onMentionedUserIdsChange([...mentionedUserIds, user.id]);
    }

    setShowDropdown(false);
    setQuery('');
    setMentionStart(null);

    // Restore cursor position after the inserted token
    requestAnimationFrame(() => {
      const pos = before.length + token.length;
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!showDropdown || filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' && showDropdown) {
      e.preventDefault();
      selectUser(filtered[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowDropdown(false);
    }
  }

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        rows={3}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
      {showDropdown && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {filtered.map((user, i) => (
            <button
              key={user.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent textarea blur
                selectUser(user);
              }}
              className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors ${
                i === selectedIndex
                  ? 'bg-brand-rose/10'
                  : 'hover:bg-slate-50'
              }`}
            >
              <div className="w-6 h-6 rounded-full bg-brand-rose/10 flex items-center justify-center text-[9px] font-bold text-brand-rose shrink-0">
                {getInitials(user.displayName)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {user.displayName}
                </p>
                <p className="text-xs text-slate-400 truncate">{user.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
